import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useChatStore } from '@/stores/chatStore'
import type { ChatSession, ChatMessage } from '@/types'

// ─── Public (Visitor) Hooks ──────────────────────────────────────────

export function useChatOnlineStatus() {
  return useQuery({
    queryKey: ['engitronix-chat-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engitronix_site_settings')
        .select('value')
        .eq('key', 'chat_settings')
        .single()

      if (error) {
        console.error('Failed to load chat settings:', error)
        return { online: false }
      }

      return data.value as { online: boolean }
    },
    refetchInterval: 30000,
    staleTime: 10000,
  })
}

export function useVisitorSession(visitorId: string) {
  return useQuery({
    queryKey: ['engitronix-chat-session', visitorId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engitronix_chat_sessions')
        .select('*')
        .eq('visitor_id', visitorId)
        .eq('status', 'open')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) {
        console.error('Failed to load visitor session:', error)
        return null
      }

      return data as ChatSession | null
    },
    enabled: !!visitorId,
  })
}

export function useVisitorMessages(sessionId: string | null) {
  return useQuery({
    queryKey: ['engitronix-chat-messages', sessionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engitronix_chat_messages')
        .select('*')
        .eq('session_id', sessionId!)
        .order('created_at', { ascending: true })

      if (error) {
        console.error('Failed to load messages:', error)
        return []
      }

      return data as ChatMessage[]
    },
    enabled: !!sessionId,
  })
}

export function useCreateSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (session: {
      visitor_id: string
      visitor_name: string | null
      visitor_email: string | null
    }) => {
      const { data, error } = await supabase
        .from('engitronix_chat_sessions')
        .insert(session)
        .select()
        .single()

      if (error) throw error
      return data as ChatSession
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['engitronix-chat-session', data.visitor_id],
      })
    },
  })
}

export function useSendVisitorMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (message: {
      session_id: string
      content: string
    }) => {
      const { data, error } = await supabase
        .from('engitronix_chat_messages')
        .insert({ ...message, sender: 'visitor' })
        .select()
        .single()

      if (error) throw error
      return data as ChatMessage
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['engitronix-chat-messages', data.session_id],
      })
    },
  })
}

// ─── Admin Hooks ─────────────────────────────────────────────────────

export function useAdminChatSessions() {
  return useQuery({
    queryKey: ['admin-engitronix-chat-sessions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engitronix_chat_sessions')
        .select('*')
        .order('updated_at', { ascending: false })

      if (error) throw error
      return data as ChatSession[]
    },
  })
}

export function useAdminChatMessages(sessionId: string | null) {
  return useQuery({
    queryKey: ['admin-engitronix-chat-messages', sessionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('engitronix_chat_messages')
        .select('*')
        .eq('session_id', sessionId!)
        .order('created_at', { ascending: true })

      if (error) throw error
      return data as ChatMessage[]
    },
    enabled: !!sessionId,
  })
}

export function useAdminSendMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (message: {
      session_id: string
      content: string
    }) => {
      const { data, error } = await supabase
        .from('engitronix_chat_messages')
        .insert({ ...message, sender: 'admin' })
        .select()
        .single()

      if (error) throw error

      // Update session's updated_at
      await supabase
        .from('engitronix_chat_sessions')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', message.session_id)

      return data as ChatMessage
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['admin-engitronix-chat-messages', data.session_id],
      })
      queryClient.invalidateQueries({
        queryKey: ['admin-engitronix-chat-sessions'],
      })
    },
  })
}

export function useAdminCloseSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from('engitronix_chat_sessions')
        .update({ status: 'closed', updated_at: new Date().toISOString() })
        .eq('id', sessionId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin-engitronix-chat-sessions'],
      })
    },
  })
}

export function useAdminReopenSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from('engitronix_chat_sessions')
        .update({ status: 'open', updated_at: new Date().toISOString() })
        .eq('id', sessionId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin-engitronix-chat-sessions'],
      })
    },
  })
}

export function useAdminMarkSessionRead(sessionId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!sessionId) return

      const { error } = await supabase
        .from('engitronix_chat_sessions')
        .update({ is_read_by_admin: true })
        .eq('id', sessionId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['admin-engitronix-chat-sessions'],
      })
    },
  })
}

export function useUpdateChatSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (settings: { online: boolean }) => {
      const { error } = await supabase
        .from('engitronix_site_settings')
        .update({ value: settings })
        .eq('key', 'chat_settings')

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['engitronix-chat-settings'],
      })
    },
  })
}

export function useAdminUnreadCount() {
  return useQuery({
    queryKey: ['admin-engitronix-chat-unread-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('engitronix_chat_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('is_read_by_admin', false)
        .eq('status', 'open')

      if (error) {
        console.error('Failed to load unread count:', error)
        return 0
      }

      return count || 0
    },
    refetchInterval: 30000,
  })
}

// ─── Realtime Hook ───────────────────────────────────────────────────

export function useChatRealtime(
  sessionId: string | null,
  mode: 'visitor' | 'admin'
) {
  const queryClient = useQueryClient()
  const { appendMessage, incrementUnread, isOpen } = useChatStore()

  useEffect(() => {
    if (!sessionId) return

    const channel = supabase
      .channel(`chat-${sessionId}-${mode}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'engitronix_chat_messages',
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const newMessage = payload.new as ChatMessage

          // Only process messages from the other party
          if (
            (mode === 'visitor' && newMessage.sender === 'admin') ||
            (mode === 'admin' && newMessage.sender === 'visitor')
          ) {
            appendMessage(newMessage)

            if (mode === 'visitor' && !isOpen) {
              incrementUnread()
            }

            // Invalidate queries to keep in sync
            if (mode === 'admin') {
              queryClient.invalidateQueries({
                queryKey: ['admin-engitronix-chat-messages', sessionId],
              })
              queryClient.invalidateQueries({
                queryKey: ['admin-engitronix-chat-sessions'],
              })
              queryClient.invalidateQueries({
                queryKey: ['admin-engitronix-chat-unread-count'],
              })
            } else {
              queryClient.invalidateQueries({
                queryKey: ['engitronix-chat-messages', sessionId],
              })
            }
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [sessionId, mode, queryClient, appendMessage, incrementUnread, isOpen])
}

// Admin-level realtime for new sessions
export function useAdminSessionsRealtime() {
  const queryClient = useQueryClient()

  useEffect(() => {
    const channel = supabase
      .channel('admin-chat-sessions')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'engitronix_chat_sessions',
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ['admin-engitronix-chat-sessions'],
          })
          queryClient.invalidateQueries({
            queryKey: ['admin-engitronix-chat-unread-count'],
          })
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'engitronix_chat_messages',
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ['admin-engitronix-chat-sessions'],
          })
          queryClient.invalidateQueries({
            queryKey: ['admin-engitronix-chat-unread-count'],
          })
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [queryClient])
}
